/**
 * Список с сервера и свои незавершённые правки (новый дизайн: реактивно и атомарно, без таймеров).
 *
 * На экране всегда «последний снимок сервера + свои правки, которые он ещё не содержит»:
 * - снимок приходит отовсюду — перечитывание, событие по вебсокету, ответ после загрузки — и несёт ревизию
 *   (сервер: migrations/168_project_file_revisions.sql); снимок старее уже принятого отбрасывается, поэтому
 *   запоздавшее эхо не откатывает экран;
 * - своя правка накладывается сразу; сервер ответил — правка помнит ревизию своей записи и снимается, как только
 *   пришёл снимок не старее её; ошибка — правка снимается, и на экране остаётся снимок сервера с чужими изменениями.
 * Так быстрые правки подряд не моргают, чужие изменения не теряются, а откат не возвращает устаревшую копию списка.
 */
import type { QueryClient } from '@tanstack/react-query';

type Change = { apply: (list: never[]) => unknown[]; confirmedAt: number | null };
type Entry = {
  server: unknown[] | null;
  revision: number | null;
  changes: Map<number, Change>;
  /** Сохранить ссылки неизменённых строк (меньше перерисовок) — как в обработчике событий */
  merge?: (prev: unknown[] | undefined, next: unknown[]) => unknown[];
  /** Сколько своих созданий ждут ответа (holdServerList): снимки принимаются, но экран строится от heldBase */
  holds: number;
  /** Снимок, который был на экране, когда начали придерживать: свои правки рисуются поверх него */
  heldBase: unknown[] | null;
};

const entries = new Map<string, Entry>();
let seq = 0;

const keyOf = (queryKey: readonly unknown[]) => JSON.stringify(queryKey);

function entryOf(queryKey: readonly unknown[]): Entry {
  const key = keyOf(queryKey);
  let e = entries.get(key);
  if (!e) {
    e = { server: null, revision: null, changes: new Map(), holds: 0, heldBase: null };
    entries.set(key, e);
  }
  return e;
}

function viewOf(e: Entry, base: unknown[]): unknown[] {
  let out = base;
  for (const c of e.changes.values()) out = c.apply(out as never[]);
  return out;
}

function render(qc: QueryClient, queryKey: readonly unknown[], e: Entry): void {
  const base = e.holds > 0 ? e.heldBase : e.server;
  if (!base) return;
  const next = viewOf(e, base);
  qc.setQueryData<unknown[]>(queryKey, (prev) => (e.merge ? e.merge(prev, next) : next));
}

/** Правка снята сервером: пришёл снимок не старее ревизии её записи */
function dropConfirmed(e: Entry): void {
  // Пока снимки придержаны, экран строится от старого — подтверждённая правка нужна на нём до отпускания
  if (e.holds > 0) return;
  for (const [id, c] of e.changes) {
    if (c.confirmedAt !== null && (e.revision === null || c.confirmedAt <= e.revision)) e.changes.delete(id);
  }
}

/** Принять снимок; старее уже принятого — false (не применён) */
function store(e: Entry, list: unknown[], revision: number | null): boolean {
  if (revision !== null && e.revision !== null && revision < e.revision) return false;
  e.server = list;
  e.revision = revision;
  dropConfirmed(e);
  return true;
}

export type ListChange = {
  /** Сервер записал правку: revision — ревизия этой записи; null — не известна (снимется со следующим более новым снимком) */
  confirm: (revision: number | null) => void;
  /** Правка не записана: снять, на экране — снимок сервера */
  cancel: () => void;
};

/** Начать свою правку списка: сразу на экране, поверх любого следующего снимка — пока сервер её не подтвердит снимком */
export function beginListChange<T>(qc: QueryClient, queryKey: readonly unknown[], apply: (list: T[]) => T[]): ListChange {
  const e = entryOf(queryKey);
  const id = ++seq;
  const change: Change = { apply: apply as unknown as Change['apply'], confirmedAt: null };
  e.changes.set(id, change);
  if (e.server) render(qc, queryKey, e);
  else qc.setQueryData<T[]>(queryKey, (prev) => apply(prev ?? []));
  return {
    confirm: (revision) => {
      if (!e.changes.has(id)) return;
      change.confirmedAt = revision ?? (e.revision ?? 0) + 1;
      if (e.revision !== null && change.confirmedAt <= e.revision) {
        e.changes.delete(id);
        render(qc, queryKey, e);
      }
    },
    cancel: () => {
      if (!e.changes.delete(id)) return;
      render(qc, queryKey, e);
    },
  };
}

/** Снимок сервера (событие, ответ после загрузки) — в кэш со своими незавершёнными правками; устаревший — false */
export function acceptServerList<T>(
  qc: QueryClient,
  queryKey: readonly unknown[],
  list: T[],
  revision: number | null,
  merge?: (prev: T[] | undefined, next: T[]) => T[]
): boolean {
  const e = entryOf(queryKey);
  if (merge) e.merge = merge as Entry['merge'];
  if (!store(e, list, revision)) return false;
  render(qc, queryKey, e);
  return true;
}

/** Для queryFn: принять снимок и вернуть то, что показать (кэш пишет TanStack); устаревший или придержанный — текущее на экране */
export function serverListForQuery<T>(qc: QueryClient, queryKey: readonly unknown[], list: T[], revision: number | null): T[] {
  const e = entryOf(queryKey);
  if (!store(e, list, revision)) return qc.getQueryData<T[]>(queryKey) ?? (viewOf(e, e.server ?? []) as T[]);
  return viewOf(e, e.holds > 0 && e.heldBase ? e.heldBase : list) as T[];
}

/**
 * Своё создание записи ждёт ответа сервера (файл передан, сервер его сохраняет): id новой записи ещё не известен, а снимок
 * с ней может прийти раньше ответа — запись была бы на экране дважды (строка загрузки и она же из снимка). Пока придержано,
 * снимки принимаются, но экран не меняется; release — нарисовать последний снимок со своими правками (уже с новой записью).
 */
export function holdServerList(qc: QueryClient, queryKey: readonly unknown[]): () => void {
  const e = entryOf(queryKey);
  if (e.holds === 0) e.heldBase = e.server;
  e.holds += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    e.holds -= 1;
    if (e.holds === 0) {
      e.heldBase = null;
      dropConfirmed(e);
    }
    render(qc, queryKey, e);
  };
}
